"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import RotateHandle from "../ui/RotateHandle";
import { useWindowStore } from "../windows/windowStore";

type SetSquareProps = {
  windowId?: string;
  sizePx?: number; // outer square side
  angleDeg?: 45 | 30; // 45-45-90 or 30-60-90 set square
};

export default function SetSquare({
  windowId,
  sizePx = 320,
  angleDeg = 45,
}: SetSquareProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const S = sizePx;
  const margin = Math.max(8, S * 0.03);
  const borderW = Math.max(1, S * 0.004);
  const faceOpacity = 0.06;
  const labelFont = Math.max(10, S * 0.035);

  // Physical baseline (match ruler): 1in = 96 CSS px
  const pxPerInch = 96;
  const pxPerCm = pxPerInch / 2.54;
  const pxPerMm = pxPerCm / 10;

  // Leg lengths by variant: 45° => equal legs; 30° => height ≈ base/√3
  const legX = S;
  const legY = angleDeg === 45 ? S : Math.round(S / Math.sqrt(3));

  // Tick lengths and strokes to match ruler visuals
  const tickLenCm = Math.max(10, S * 0.065);
  const tickLenHalf = Math.max(8, tickLenCm * 0.66);
  const tickLenMm = Math.max(6, tickLenCm * 0.5);
  const swMajor = 1.2;
  const swMedium = 1.0;
  const swMinor = 0.8;

  const [size, setSize] = useState({
    width: S + margin * 2,
    height: S + margin * 2,
  });
  const rotationDeg = useWindowStore((s) =>
    windowId ? s.windows[windowId]?.rotationDeg ?? 0 : 0
  );
  const updateRotation = useWindowStore((s) => s.updateRotation);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (!cr) return;
      setSize((s) => ({
        width: Math.max(cr.width, S + margin * 2),
        height: s.height,
      }));
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [S, margin]);

  // Define triangle points (right triangle aligned with bottom-left baseline)
  const x0 = margin; // right angle corner
  const y0 = margin + legY;
  const x1 = margin + legX; // base end
  const y1 = margin + legY;
  const x2 = margin; // vertical end
  const y2 = margin;

  // Inner cutout: inset a small right triangle near the right angle
  const frame = Math.max(12, S * 0.1);
  const innerB_x = Math.min(x1 - 1, x0 + frame);
  const innerB_y = y0;
  const innerL_x = x0;
  const innerL_y = Math.max(y2 + 1, y0 - frame);
  const innerC_x = innerB_x;
  const innerC_y = innerL_y;

  // Build cm/mm ticks along legs based on pxPerMm (match ruler)
  const legTicks = useMemo(() => {
    type Kind = "cm" | "halfCm" | "mm";
    const bottom: { x: number; y: number; kind: Kind; label?: string }[] = [];
    const left: { x: number; y: number; kind: Kind; label?: string }[] = [];
    const stepsBottom = Math.floor(legX / pxPerMm);
    for (let i = 0; i <= stepsBottom; i++) {
      const pos = i * pxPerMm;
      const x = x0 + pos;
      const isCm = i % 10 === 0;
      const isHalf = !isCm && i % 5 === 0;
      const kind: Kind = isCm ? "cm" : isHalf ? "halfCm" : "mm";
      bottom.push({
        x,
        y: y0,
        kind,
        label: isCm ? `${i / 10}`.replace(/\.0$/, "") : undefined,
      });
    }
    const stepsLeft = Math.floor(legY / pxPerMm);
    for (let i = 0; i <= stepsLeft; i++) {
      const pos = i * pxPerMm;
      const y = y0 - pos;
      const isCm = i % 10 === 0;
      const isHalf = !isCm && i % 5 === 0;
      const kind: Kind = isCm ? "cm" : isHalf ? "halfCm" : "mm";
      left.push({
        x: x0,
        y,
        kind,
        label: isCm ? `${i / 10}`.replace(/\.0$/, "") : undefined,
      });
    }
    return { bottom, left };
  }, [legX, legY, pxPerMm, x0, y0]);

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full select-none text-foreground"
      style={{ background: "transparent" }}
      aria-label="Set Square"
    >
      {windowId && (
        <RotateHandle
          className="no-drag absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-card/70 border border-border shadow"
          title="Rotate"
          initialDeg={rotationDeg}
          getCenter={() => {
            const root = containerRef.current;
            if (!root) return null;
            const rect = root.getBoundingClientRect();
            return {
              x: rect.left + rect.width / 2,
              y: rect.top + rect.height / 2,
            };
          }}
          onChange={(deg) => updateRotation(windowId, deg)}
        />
      )}
      <svg
        width={size.width}
        height={size.height}
        role="img"
        aria-label="Set square graphic"
        style={{ display: "block" }}
      >
        <g shapeRendering="geometricPrecision">
          {/* Outer triangle with inner cutout using even-odd rule */}
          <path
            d={`M ${x0} ${y0} L ${x1} ${y1} L ${x2} ${y2} Z M ${innerC_x} ${innerC_y} L ${innerB_x} ${innerB_y} L ${innerL_x} ${innerL_y} Z`}
            fill={`rgba(255,255,255,${faceOpacity})`}
            fillRule="evenodd"
            stroke="currentColor"
            strokeOpacity={0.6}
            strokeWidth={borderW}
          />

          {/* Baseline guide */}
          <line
            x1={x0}
            y1={y0}
            x2={x1}
            y2={y1}
            stroke="currentColor"
            strokeOpacity={0.35}
            strokeWidth={borderW}
          />
          {/* Vertical guide */}
          <line
            x1={x0}
            y1={y0}
            x2={x2}
            y2={y2}
            stroke="currentColor"
            strokeOpacity={0.35}
            strokeWidth={borderW}
          />

          {/* Ticks on bottom leg (cm/5mm/mm) */}
          <g
            vectorEffect="non-scaling-stroke"
            shapeRendering="crispEdges"
            strokeLinecap="butt"
          >
            {legTicks.bottom.map((t, i) => {
              const len =
                t.kind === "cm"
                  ? tickLenCm
                  : t.kind === "halfCm"
                  ? tickLenHalf
                  : tickLenMm;
              return (
                <line
                  key={`b-${i}`}
                  x1={t.x}
                  y1={t.y}
                  x2={t.x}
                  y2={t.y - len}
                  stroke="currentColor"
                  strokeOpacity={0.8}
                  strokeWidth={
                    t.kind === "cm"
                      ? swMajor
                      : t.kind === "halfCm"
                      ? swMedium
                      : swMinor
                  }
                />
              );
            })}
          </g>

          {/* Ticks on left leg (cm/5mm/mm) */}
          <g
            vectorEffect="non-scaling-stroke"
            shapeRendering="crispEdges"
            strokeLinecap="butt"
          >
            {legTicks.left.map((t, i) => {
              const len =
                t.kind === "cm"
                  ? tickLenCm
                  : t.kind === "halfCm"
                  ? tickLenHalf
                  : tickLenMm;
              return (
                <line
                  key={`l-${i}`}
                  x1={t.x}
                  y1={t.y}
                  x2={t.x + len}
                  y2={t.y}
                  stroke="currentColor"
                  strokeOpacity={0.8}
                  strokeWidth={
                    t.kind === "cm"
                      ? swMajor
                      : t.kind === "halfCm"
                      ? swMedium
                      : swMinor
                  }
                />
              );
            })}
          </g>

          {/* Labels on legs at each cm (skip 0) */}
          {legTicks.bottom.map((t, i) => {
            if (t.kind !== "cm" || t.label === "0") return null;
            return (
              <text
                key={`bl-${i}`}
                x={t.x}
                y={t.y - tickLenCm - 6}
                fontSize={labelFont}
                textAnchor="middle"
                dominantBaseline="text-after-edge"
                fill="currentColor"
                fillOpacity={0.85}
              >
                {t.label}
              </text>
            );
          })}
          {legTicks.left.map((t, i) => {
            if (t.kind !== "cm" || t.label === "0") return null;
            return (
              <text
                key={`ll-${i}`}
                x={t.x + tickLenCm + 6}
                y={t.y}
                fontSize={labelFont}
                textAnchor="start"
                dominantBaseline="middle"
                fill="currentColor"
                fillOpacity={0.85}
              >
                {t.label}
              </text>
            );
          })}
        </g>
      </svg>
    </div>
  );
}
